import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  cleanSpecialCharacters,
  getBenefitName,
  getBenefitTarget,
  parseFloatToFixed,
} from "../../../extras";
import FinanceForm from "./NewOrderModal/financeForm";

// MUI
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Badge from "@mui/material/Badge";
import Modal from "@mui/material/Modal";
import Fade from "@mui/material/Fade";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import { alpha, useTheme } from "@mui/material/styles";

// Icons
import PaidIcon from "@mui/icons-material/Paid";
import PaymentsIcon from "@mui/icons-material/Payments";
import LanguageIcon from "@mui/icons-material/Language";
import ReceiptIcon from "@mui/icons-material/Receipt";
import DiscountIcon from "@mui/icons-material/Discount";
import EditNoteIcon from "@mui/icons-material/EditNote";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import ShoppingBagIcon from "@mui/icons-material/ShoppingBag";
import DeliveryDiningIcon from "@mui/icons-material/DeliveryDining";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import PaymentsTwoToneIcon from "@mui/icons-material/PaymentsTwoTone";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

import { LightGreyBox } from "../../styled";
import { CustomModalRoot } from "../../styled/modal";
import ModalHeader from "../../modals/modalHeader";
import { formatFinance } from "../../../utils/formatters";

// Shared panel primitives
import {
  Panel,
  PanelRow,
  SectionBar,
  SectionBarLabel,
  RowLabel,
  ValueBadge,
  TotalRow,
  PanelActionBtn,
} from "../../styled/panel";

// ─── Payment Method Row ─────────────────────────────────────────────────────

function PaymentRow({ payment, theme }) {
  const name = cleanSpecialCharacters(
    payment.payment_method || payment.value
  );
  const value = parseFloat(payment.amount || payment.value);
  const isOnline = payment.payment_type === "ONLINE";
  const isCash = payment.payment_method === "DINHEIRO";

  const color = isCash
    ? theme.palette.success
    : isOnline
    ? theme.palette.info
    : theme.palette.primary;

  const icon = isCash ? (
    <PaymentsIcon sx={{ fontSize: 14 }} />
  ) : isOnline ? (
    <LanguageIcon sx={{ fontSize: 14 }} />
  ) : (
    <CreditCardIcon sx={{ fontSize: 14 }} />
  );

  return (
    <PanelRow>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flex: 1 }}>
        <Box sx={{ display: "flex", color: color.main }}>
          {icon}
        </Box>
        <Typography
          sx={{
            fontSize: "0.82rem",
            fontWeight: 500,
            color: theme.palette.text.primary,
          }}
        >
          {isOnline ? "ONLINE" : name}
        </Typography>
        {isOnline && (
          <Chip
            label="Online"
            size="small"
            sx={{
              height: 18,
              fontSize: "0.6rem",
              fontWeight: 700,
              backgroundColor: alpha(theme.palette.info.main, 0.1),
              color: theme.palette.info.dark,
            }}
          />
        )}
      </Box>
      <ValueBadge colorKey="success">
        {formatFinance(value)}
      </ValueBadge>
    </PanelRow>
  );
}

// ─── Main Component ─────────────────────────────────────────────────────────

const FinancialInfos = ({
  paymentMethods,
  paymentPending,
  setPaymentMethods,
  subTotal,
  total,
  addBenefits,
  removeBenefits,
  benefits,
  voucherDiscount,
  deliveryTax,
  tax,
  serviceFeeAmount,
  changeFor,
  orderStatus,
  orderChannel,
  loadData,
}) => {
  const theme = useTheme();
  const [change, setChange] = useState(0);
  const [changeForState, setChangeForState] = useState(0);
  const [totalToPay, setTotalToPay] = useState(0);
  const [showDiscounts, setShowDiscounts] = useState(false);
  const [showPayments, setShowPayments] = useState(false);

  const isEditable = orderChannel === "SLAPIE_POS" && orderStatus < 34;

  const validBenefits = useMemo(
    () => benefits?.filter((b) => Number(b.value) > 0) || [],
    [benefits]
  );

  const calculateTotalToPay = useCallback(() => {
    const parsedTotal = +total;
    return Math.round(parsedTotal * 100) / 100;
  }, [total, voucherDiscount]);

  const calculateChanges = useCallback(() => {
    const changeForAux = paymentMethods?.find(
      (element) => parseFloatToFixed(element.changeFor) > 0
    )?.changeFor;

    const parsedChangeForAux = parseFloatToFixed(changeForAux || "0");
    setChangeForState(parsedChangeForAux);
    setChange(parsedChangeForAux - total);
  }, [paymentMethods, total]);

  useEffect(() => {
    setTotalToPay(calculateTotalToPay());
  }, [calculateTotalToPay]);

  useEffect(() => {
    setTotalToPay(calculateTotalToPay());
    if (paymentMethods?.length === 0) {
      setChange(0);
      setChangeForState(0);
    } else {
      calculateChanges();
    }
  }, [paymentMethods, calculateTotalToPay, calculateChanges]);

  const toggleShowPayments = useCallback(() => {
    setShowPayments((prev) => {
      const next = !prev;
      if (!next) loadData();
      return next;
    });
  }, [loadData]);

  const handleRemoveBenefit = useCallback(
    (benefitId) => {
      if (removeBenefits) removeBenefits(benefitId);
    },
    [removeBenefits]
  );

  // ─── Breakdown items ──────────────────────────────────────────────────

  const breakdownItems = [
    { label: "Subtotal", icon: <ShoppingBagIcon />, value: subTotal, colorKey: "normal" },
    tax > 0 && { label: "Adicional", icon: <ReceiptIcon />, value: tax, colorKey: "normal" },
    deliveryTax > 0 && { label: "Tx. Entrega", icon: <DeliveryDiningIcon />, value: deliveryTax, colorKey: "normal" },
    serviceFeeAmount > 0 && { label: "Tx. Serviço", icon: <ReceiptIcon />, value: serviceFeeAmount, colorKey: "normal" },
    voucherDiscount > 0 && { label: "Descontos", icon: <DiscountIcon />, value: voucherDiscount, colorKey: "success" },
  ].filter(Boolean);

  // ─── Render ───────────────────────────────────────────────────────────

  return (
    <>
      <Box>
        <Panel>
          {/* ── Total ── */}
          <TotalRow>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              <PaidIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />
              <Typography
                sx={{
                  fontSize: "0.88rem",
                  fontWeight: 700,
                  color: theme.palette.primary.dark,
                }}
              >
                Total a Pagar
              </Typography>
            </Box>
            <ValueBadge colorKey="primary" sx={{ fontSize: "0.92rem", fontWeight: 700 }}>
              {formatFinance(parseFloat(total))}
            </ValueBadge>
          </TotalRow>

          {/* ── Payment pending / change ── */}
          {paymentPending > 0 && (
            <PanelRow>
              <RowLabel>Cobrar</RowLabel>
              <Box sx={{ flex: 1 }} />
              <ValueBadge colorKey="error">
                {formatFinance(parseFloat(paymentPending))}
              </ValueBadge>
            </PanelRow>
          )}

          {changeForState > 0 && (
            <>
              <PanelRow>
                <RowLabel>Troco para</RowLabel>
                <Box sx={{ flex: 1 }} />
                <ValueBadge colorKey="normal">
                  {formatFinance(changeForState)}
                </ValueBadge>
              </PanelRow>
              <PanelRow>
                <RowLabel>Troco</RowLabel>
                <Box sx={{ flex: 1 }} />
                <ValueBadge colorKey="success">
                  {formatFinance(change)}
                </ValueBadge>
              </PanelRow>
            </>
          )}

          {/* ── Breakdown ── */}
          <SectionBar>
            <SectionBarLabel>Detalhes do Valor</SectionBarLabel>
          </SectionBar>

          {breakdownItems.map((item, i) => (
            <PanelRow key={i}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flex: 1 }}>
                <Box sx={{ display: "flex", color: theme.palette.text.disabled }}>
                  {React.cloneElement(item.icon, { sx: { fontSize: 15 } })}
                </Box>
                <Typography
                  sx={{
                    fontSize: "0.82rem",
                    fontWeight: 400,
                    color: theme.palette.text.primary,
                  }}
                >
                  {item.label}
                </Typography>
              </Box>
              <ValueBadge colorKey={item.colorKey}>
                {formatFinance(parseFloat(item.value))}
              </ValueBadge>
            </PanelRow>
          ))}

          {/* ── Payment methods ── */}
          <SectionBar>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              <CreditCardIcon sx={{ fontSize: 14 }} />
              <SectionBarLabel>Pagamentos</SectionBarLabel>
            </Box>
            {isEditable && paymentMethods?.length > 0 && (
              <PanelActionBtn
                size="small"
                startIcon={<EditNoteIcon sx={{ fontSize: "14px !important" }} />}
                onClick={toggleShowPayments}
              >
                Editar
              </PanelActionBtn>
            )}
          </SectionBar>

          {!paymentMethods || paymentMethods.length === 0 ? (
            <PanelRow
              sx={{
                flexDirection: "column",
                alignItems: "center",
                py: 2,
                gap: 0.75,
              }}
            >
              <PaymentsTwoToneIcon
                sx={{ fontSize: 28, color: theme.palette.warning.dark, opacity: 0.7 }}
              />
              <Typography
                sx={{
                  fontSize: "0.78rem",
                  fontWeight: 500,
                  color: theme.palette.text.secondary,
                }}
              >
                Nenhum pagamento registrado
              </Typography>
              {isEditable && (
                <PanelActionBtn
                  size="small"
                  startIcon={<AddCircleOutlineIcon sx={{ fontSize: "14px !important" }} />}
                  onClick={toggleShowPayments}
                  sx={{
                    mt: 0.5,
                    color: theme.palette.warning.dark,
                    backgroundColor: alpha(theme.palette.warning.main, 0.08),
                    "&:hover": {
                      backgroundColor: alpha(theme.palette.warning.main, 0.15),
                    },
                  }}
                >
                  Adicionar
                </PanelActionBtn>
              )}
            </PanelRow>
          ) : (
            paymentMethods.map((payment) => (
              <PaymentRow
                key={`payment-${payment.id}`}
                payment={payment}
                theme={theme}
              />
            ))
          )}

          {/* ── Discounts / benefits ── */}
          {(validBenefits.length > 0 || isEditable) && (
            <>
              <SectionBar>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                    cursor: "pointer",
                    flex: 1,
                  }}
                  onClick={() => setShowDiscounts((p) => !p)}
                >
                  <Badge
                    color="success"
                    variant="dot"
                    invisible={validBenefits.length === 0}
                  >
                    <DiscountIcon sx={{ fontSize: 14 }} />
                  </Badge>
                  <SectionBarLabel>
                    Descontos{validBenefits.length > 0 ? ` (${validBenefits.length})` : ""}
                  </SectionBarLabel>
                  <IconButton size="small" sx={{ width: 20, height: 20, ml: "auto" }}>
                    {showDiscounts ? (
                      <ExpandLessIcon sx={{ fontSize: 16 }} />
                    ) : (
                      <ExpandMoreIcon sx={{ fontSize: 16 }} />
                    )}
                  </IconButton>
                </Box>

                {isEditable && (
                  <PanelActionBtn
                    size="small"
                    startIcon={<AddCircleOutlineIcon sx={{ fontSize: "14px !important" }} />}
                    onClick={toggleShowPayments}
                  >
                    Adicionar
                  </PanelActionBtn>
                )}
              </SectionBar>

              {showDiscounts && (
                <>
                  {validBenefits.length === 0 ? (
                    <PanelRow sx={{ justifyContent: "center" }}>
                      <Typography
                        sx={{
                          fontSize: "0.78rem",
                          color: theme.palette.text.disabled,
                        }}
                      >
                        Nenhum desconto aplicado
                      </Typography>
                    </PanelRow>
                  ) : (
                    validBenefits.map((benefit) => (
                      <PanelRow key={`benefit-${benefit.id}`}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            sx={{
                              fontSize: "0.82rem",
                              fontWeight: 500,
                              color: theme.palette.success.dark,
                            }}
                          >
                            {getBenefitName(benefit.name)}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "0.68rem",
                              color: theme.palette.text.disabled,
                              mt: 0.1,
                            }}
                          >
                            Aplicado em: {getBenefitTarget(benefit.target)}
                          </Typography>
                        </Box>
                        <ValueBadge colorKey="success">
                          {formatFinance(Number(benefit.value))}
                        </ValueBadge>
                        {isEditable && (
                          <Tooltip title="Remover desconto">
                            <IconButton
                              size="small"
                              onClick={() => handleRemoveBenefit(benefit.id)}
                              sx={{
                                width: 26,
                                height: 26,
                                borderRadius: 1,
                                color: theme.palette.error.main,
                                "&:hover": {
                                  backgroundColor: alpha(theme.palette.error.main, 0.08),
                                },
                              }}
                            >
                              <DeleteOutlineIcon sx={{ fontSize: 15 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </PanelRow>
                    ))
                  )}
                </>
              )}
            </>
          )}
        </Panel>
      </Box>

      {/* ── Payment/Discount Edit Modal ── */}
      <Modal
        aria-labelledby="order-info-modal"
        aria-describedby="order-info-modal"
        open={showPayments && isEditable}
        onClose={toggleShowPayments}
        closeAfterTransition
        sx={{
          zIndex: 99999,
          "&:focus-visible": { border: "none" },
        }}
      >
        <Fade in={showPayments && isEditable} unmountOnExit>
          <CustomModalRoot top={40} left={50} width={60} height={65} overflow="auto">
            <ModalHeader
              label="Gerenciar Pagamentos e Descontos"
              onClose={toggleShowPayments}
            />
            <LightGreyBox>
              <FinanceForm
                totalToPay={totalToPay}
                setPaymentMethods={setPaymentMethods}
                paymentMethods={paymentMethods}
                total={total}
                setDiscounts={addBenefits}
                removeDiscounts={removeBenefits}
                discounts={benefits}
                isEdit
              />
            </LightGreyBox>
          </CustomModalRoot>
        </Fade>
      </Modal>
    </>
  );
};

export default FinancialInfos;
